import { type ChangeEvent, memo, useCallback } from "react";
import type { Money } from "@/domain";
import { cn, components, layout } from "@/utilities";
import { CURRENCY_OPTIONS } from "@/types/core";
import { FormField } from "./form-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "./select";

interface MoneyFieldProps {
  id: string;
  label: string;
  value: Money;
  onChange: (value: Money) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  error?: string;
  disabled?: boolean;
}

export const MoneyField = memo(
  ({
    id,
    label,
    value,
    onChange,
    placeholder = "0",
    required = false,
    className,
    error,
    disabled = false
  }: MoneyFieldProps) => {
    // Handlers
    const handleAmountChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const amount = Number.isNaN(Number(e.target.value))
          ? 0
          : Number(e.target.value);
        onChange({
          ...value,
          amount
        });
      },
      [value, onChange]
    );

    const handleCurrencyChange = useCallback(
      (currency: string) => {
        onChange({
          ...value,
          currency
        });
      },
      [value, onChange]
    );

    // Render
    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>

        <div className={cn(layout.flexRowGap2)}>
          {/* Amount Field */}
          <FormField
            id={`${id}-amount`}
            type="number"
            value={value.amount?.toString() || ""}
            onChange={handleAmountChange}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            min={0}
            error={error}
            className="w-full flex-1"
          />

          {/* Currency Selector */}
          <Select
            value={value.currency || "CAD"}
            onValueChange={handleCurrencyChange}
            disabled={disabled}
          >
            <SelectTrigger
              className={cn(
                components.selectTriggerFormField,
                "w-25",
                error &&
                  "border-destructive focus-visible:ring-destructive/20"
              )}
            >
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              {CURRENCY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Error Message */}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }
);

MoneyField.displayName = "MoneyField";
